const WINDOWS_VERSIONS = {
    '10.0': '10/11',
    '6.3': '8.1',
    '6.2': '8',
    '6.1': '7',
};

function matchVersion(userAgent, expression) {
    const match = userAgent.match(expression);

    return match?.[1]?.replaceAll('_', '.') || 'Não informada';
}

export function detectOperatingSystem(
    userAgent = globalThis.navigator?.userAgent || '',
    navigatorLike = globalThis.navigator || {}
) {
    if (/Android/i.test(userAgent)) {
        return {
            name: 'Android',
            version: matchVersion(userAgent, /Android\s([\d.]+)/i),
        };
    }

    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return {
            name: 'iOS',
            version: matchVersion(userAgent, /OS\s([\d_]+)/i),
        };
    }

    if (
        /Macintosh/i.test(userAgent)
        && Number(navigatorLike.maxTouchPoints) > 1
    ) {
        return {
            name: 'iPadOS',
            version: matchVersion(userAgent, /Version\/([\d.]+)/i),
        };
    }

    if (/Windows NT/i.test(userAgent)) {
        const rawVersion = matchVersion(userAgent, /Windows NT\s([\d.]+)/i);

        return {
            name: 'Windows',
            version: WINDOWS_VERSIONS[rawVersion] || rawVersion,
        };
    }

    if (/Mac OS X/i.test(userAgent)) {
        return {
            name: 'macOS',
            version: matchVersion(userAgent, /Mac OS X\s([\d_]+)/i),
        };
    }

    if (/Linux/i.test(userAgent)) {
        return { name: 'Linux', version: 'Não informada' };
    }

    return {
        name: navigatorLike.platform || 'Desconhecido',
        version: 'Não informada',
    };
}

export function detectBrowser(
    userAgent = globalThis.navigator?.userAgent || ''
) {
    const definitions = [
        ['Samsung Internet', /SamsungBrowser\/([\d.]+)/i],
        ['Edge', /EdgA?\/([\d.]+)/i],
        ['Edge', /EdgiOS\/([\d.]+)/i],
        ['Firefox', /Firefox\/([\d.]+)/i],
        ['Firefox', /FxiOS\/([\d.]+)/i],
        ['Chrome', /Chrome\/([\d.]+)/i],
        ['Chrome', /CriOS\/([\d.]+)/i],
        ['Safari', /Version\/([\d.]+).*Safari/i],
    ];

    for (const [name, expression] of definitions) {
        const version = matchVersion(userAgent, expression);

        if (version !== 'Não informada') {
            return { name, version };
        }
    }

    return { name: 'Desconhecido', version: 'Não informada' };
}

export async function getCameraPermissionStatus(
    navigatorLike = globalThis.navigator
) {
    if (!navigatorLike?.permissions?.query) {
        return 'API de permissão indisponível';
    }

    try {
        const status = await navigatorLike.permissions.query({
            name: 'camera',
        });

        return status.state || 'Não informada';
    } catch {
        return 'Não consultável neste navegador';
    }
}

async function getClientHints(navigatorLike) {
    if (!navigatorLike?.userAgentData?.getHighEntropyValues) {
        return {};
    }

    try {
        return await navigatorLike.userAgentData.getHighEntropyValues([
            'platform',
            'platformVersion',
            'fullVersionList',
        ]);
    } catch {
        return {};
    }
}

function applyClientHints(operatingSystem, browser, hints) {
    const nextOperatingSystem = { ...operatingSystem };
    const nextBrowser = { ...browser };

    if (
        hints.platform === 'Android'
        && String(hints.platformVersion || '').trim()
    ) {
        nextOperatingSystem.name = 'Android';
        nextOperatingSystem.version = hints.platformVersion;
    }

    const browserBrands = {
        Edge: ['Microsoft Edge'],
        Chrome: ['Google Chrome', 'Chromium'],
    };
    const matchingBrand = browserBrands[nextBrowser.name]?.flatMap(
        (brand) => (
            hints.fullVersionList?.filter((item) => item.brand === brand)
            || []
        )
    )?.[0];

    if (matchingBrand?.version) {
        nextBrowser.version = matchingBrand.version;
    }

    return {
        operatingSystem: nextOperatingSystem,
        browser: nextBrowser,
    };
}

export async function collectEnvironment({
    windowLike = globalThis.window,
    navigatorLike = globalThis.navigator,
    zxingAvailable = true,
} = {}) {
    const userAgent = navigatorLike?.userAgent || '';
    const mediaDevices = navigatorLike?.mediaDevices;
    const clientHints = await getClientHints(navigatorLike);
    const identified = applyClientHints(
        detectOperatingSystem(userAgent, navigatorLike),
        detectBrowser(userAgent),
        clientHints
    );

    return {
        collectedAt: new Date().toISOString(),
        operatingSystem: identified.operatingSystem,
        browser: identified.browser,
        secureContext: Boolean(windowLike?.isSecureContext),
        protocol: windowLike?.location?.protocol || 'Não informado',
        mediaDevices: Boolean(mediaDevices),
        cameraApi: Boolean(mediaDevices?.getUserMedia),
        enumerateDevices: Boolean(mediaDevices?.enumerateDevices),
        barcodeDetector: typeof windowLike?.BarcodeDetector === 'function',
        zxing: Boolean(zxingAvailable),
        cameraPermission: await getCameraPermissionStatus(navigatorLike),
        language: navigatorLike?.language || 'Não informada',
        platform: navigatorLike?.platform || 'Não informada',
        userAgent,
        clientHints,
        supportedConstraints:
            mediaDevices?.getSupportedConstraints?.() || {},
    };
}
